import { useEffect } from 'react';
import { router } from '../router';

const PORTAL_ID = '149257031';
const SCRIPT_SRC = `https://js-eu1.hs-scripts.com/${PORTAL_ID}.js`;
const SCRIPT_ID = 'hs-script-loader';
const CONSENT_KEY = 'halr_cookie_consent_v1';
const CONSENT_EVENT = 'halr-cookie-consent';

const PUBLIC_PATHS = new Set(['/', '/login']);

type HubSpotWidget = {
  load: () => void;
  remove: () => void;
};

declare global {
  interface Window {
    hsConversationsSettings?: { loadImmediately?: boolean };
    hsConversationsOnReady?: Array<() => void>;
    HubSpotConversations?: { widget?: HubSpotWidget };
  }
}

function consentAllowsChat(): boolean {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as { analytics?: boolean };
    return parsed.analytics === true;
  } catch {
    return false;
  }
}

let chatAllowed = false;
let readyHooked = false;

function showWidget() {
  chatAllowed = true;
  window.hsConversationsSettings = { loadImmediately: false };
  if (!document.getElementById(SCRIPT_ID)) {
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.async = true;
    script.defer = true;
    script.src = SCRIPT_SRC;
    document.body.appendChild(script);
  }
  const load = () => {
    if (!chatAllowed) return;
    window.HubSpotConversations?.widget?.load();
  };
  if (window.HubSpotConversations?.widget) {
    load();
    return;
  }
  if (readyHooked) return;
  readyHooked = true;
  window.hsConversationsOnReady = window.hsConversationsOnReady || [];
  window.hsConversationsOnReady.push(load);
}

function hideWidget() {
  chatAllowed = false;
  window.HubSpotConversations?.widget?.remove();
  window.setTimeout(() => {
    if (!chatAllowed) window.HubSpotConversations?.widget?.remove();
  }, 400);
}

/**
 * HubSpot's live-chat launcher on the marketing home and the login page.
 * The in-app Help button is a separate ticket form and is not this widget.
 * The script stays off until cookie consent, and off every other route.
 */
export default function PublicLiveChat() {
  useEffect(() => {
    const apply = () => {
      const path = router.state.location.pathname;
      if (PUBLIC_PATHS.has(path) && consentAllowsChat()) showWidget();
      else hideWidget();
    };
    apply();
    const unsubscribe = router.subscribe(apply);
    window.addEventListener(CONSENT_EVENT, apply);
    return () => {
      window.removeEventListener(CONSENT_EVENT, apply);
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, []);

  return null;
}
